<script>
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { Size } from "@/abc-dsk/options";
import {
  DIRECTION_DANISH_ENGLISH,
  DIRECTION_DANISH_FRENCH,
  DIRECTION_DANISH_GERMAN,
  DIRECTION_ENGLISH_DANISH,
  DIRECTION_FRENCH_DANISH,
  DIRECTION_GERMAN_DANISH
} from "@/common/direction-types";
import { mapGendersByLevel } from "@/common/level";
import EnglishAdjectiveInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/english/AdjectiveInflectionSchemeModal.vue";
import EnglishNounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/english/NounInflectionSchemeModal.vue";
import EnglishVerbInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/english/VerbInflectionSchemeModal.vue";
import FrenchAdjectiveInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/french/AdjectiveInflectionSchemeModal.vue";
import FrenchNounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/french/NounInflectionSchemeModal.vue";
import FrenchVerbInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/french/VerbInflectionSchemeModal.vue";
import GermanAdjectiveInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/AdjectiveInflectionSchemeModal.vue";
import GermanArticleInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/ArticleInflectionSchemeModal.vue";
import GermanNounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/NounInflectionSchemeModal.vue";
import GermanPronounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/PronounInflectionSchemeModal.vue";
import GermanVerbInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/VerbInflectionSchemeModal.vue";
import { mapGrammaticalLabelsToLevel } from "@/components/article/dictsys/level-mapping";
import IconWhiteDocumentNoBorder from "@/components/icons/white/IconWhiteDocumentNoBorder.vue";
import lang from "@/directives/lang";
import { useInflectionSchemeStore, useSearchStore, useSettingsStore } from "@/stores";

export default defineComponent({
  name: "InflectionScheme",
  components: {
    EnglishAdjectiveInflectionSchemeModal,
    EnglishNounInflectionSchemeModal,
    EnglishVerbInflectionSchemeModal,
    FrenchAdjectiveInflectionSchemeModal,
    FrenchNounInflectionSchemeModal,
    FrenchVerbInflectionSchemeModal,
    GermanAdjectiveInflectionSchemeModal,
    GermanArticleInflectionSchemeModal,
    GermanNounInflectionSchemeModal,
    GermanPronounInflectionSchemeModal,
    GermanVerbInflectionSchemeModal,
    IconWhiteDocumentNoBorder,
    TgModal
  },
  directives: {
    lang
  },
  data() {
    return {
      wordClassMap: {
        danish: {
          adjective: "tillægsord",
          article: "kendeord",
          noun: "navneord",
          pronoun: "stedord",
          verb: "udsagnsord"
        },
        latin: {
          adjective: "adjektiv",
          article: "artikel",
          noun: "substantiv",
          pronoun: "pronomen",
          verb: "verbum"
        }
      }
    };
  },
  computed: {
    ...mapStores(useInflectionSchemeStore, useSearchStore, useSettingsStore),

    searchDirection() {
      return this.searchStore.searchDirection;
    },

    articleFont() {
      return this.settingsStore.userPreferences.articleFont;
    },

    wordclassLevel() {
      return this.settingsStore.activeWordClassLevel;
    },

    inflectionSchemeData() {
      return this["inflection-schemeStore"].inflectionSchemeData;
    },
    isInflectionSchemeOpen() {
      return this["inflection-schemeStore"].isInflectionSchemeOpen;
    },
    inflectionLanguage() {
      return this["inflection-schemeStore"].inflectionSchemeLanguage;
    },

    inflectionSchemeModalName() {
      const { inflectionScheme } = this.inflectionSchemeData;
      const data = inflectionScheme.json;

      if (!data) {
        return undefined;
      }

      if ([DIRECTION_DANISH_ENGLISH, DIRECTION_ENGLISH_DANISH].includes(this.searchDirection)) {
        switch (true) {
          case "adjective" in data:
            return "EnglishAdjectiveInflectionSchemeModal";
          case "noun" in data:
            return "EnglishNounInflectionSchemeModal";
          case "verb" in data:
            return "EnglishVerbInflectionSchemeModal";
        }
      }

      if ([DIRECTION_DANISH_FRENCH, DIRECTION_FRENCH_DANISH].includes(this.searchDirection)) {
        switch (true) {
          case "adjective" in data:
            return "FrenchAdjectiveInflectionSchemeModal";
          case "noun" in data:
            return "FrenchNounInflectionSchemeModal";
          case "verb" in data:
            return "FrenchVerbInflectionSchemeModal";
        }
      }

      if ([DIRECTION_DANISH_GERMAN, DIRECTION_GERMAN_DANISH].includes(this.searchDirection)) {
        switch (true) {
          case "adjective" in data:
            return "GermanAdjectiveInflectionSchemeModal";
          case "article" in data:
            return "GermanArticleInflectionSchemeModal";
          case "noun" in data:
            return "GermanNounInflectionSchemeModal";
          case "verb" in data:
            return "GermanVerbInflectionSchemeModal";
          case "pronoun" in data:
            return "GermanPronounInflectionSchemeModal";
        }
      }

      return undefined;
    },

    wordClass() {
      // We can't rely on the .wordclass property, since it can be empty.
      switch (this.inflectionSchemeModalName) {
        case "GermanArticleInflectionSchemeModal":
          return "article";

        case "EnglishAdjectiveInflectionSchemeModal":
        case "GermanAdjectiveInflectionSchemeModal":
        case "FrenchAdjectiveInflectionSchemeModal":
          return "adjective";

        case "EnglishVerbInflectionSchemeModal":
        case "FrenchVerbInflectionSchemeModal":
        case "GermanVerbInflectionSchemeModal":
          return "verb";

        case "EnglishNounInflectionSchemeModal":
        case "FrenchNounInflectionSchemeModal":
        case "GermanNounInflectionSchemeModal":
          return "noun";

        case "GermanPronounInflectionSchemeModal":
          return "pronoun";

        default:
          return undefined;
      }
    },
    wordClassForLevel() {
      const { danish, latin } = this.wordClassMap;
      return mapGrammaticalLabelsToLevel(
        this.wordclassLevel,
        danish[this.wordClass],
        latin[this.wordClass]
      );
    },
    gendersForLevel() {
      const { genders } = this.inflectionSchemeData.metadata;
      return genders
        ? mapGendersByLevel(
            genders,
            this.wordclassLevel,
            this.settingsStore.activeLanguageGroup ?? ""
          ).join("/")
        : "";
    },

    unwrappedInflectionScheme() {
      const keys = Object.keys(this.inflectionSchemeData.inflectionScheme.json ?? {});

      if (keys.length === 1) {
        return this.inflectionSchemeData.inflectionScheme.json[keys[0]];
      }

      if (keys.includes("verb")) {
        return this.inflectionSchemeData.inflectionScheme.json;
      }

      throw Error(`No inflection scheme for lemmaId ${this.inflectionSchemeData.id}`);
    },

    size() {
      switch (this.inflectionSchemeModalName) {
        case "GermanAdjectiveInflectionSchemeModal":
        case "GermanPronounInflectionSchemeModal":
          return Size.Xl;

        case "FrenchVerbInflectionSchemeModal":
        case "EnglishVerbInflectionSchemeModal":
        case "GermanVerbInflectionSchemeModal":
          return Size.Lg;

        default:
          return Size.Md;
      }
    }
  },
  methods: {
    closeInflectionScheme() {
      this["inflection-schemeStore"].closeInflectionScheme();
    }
  }
});
</script>

<template>
  <div v-if="inflectionSchemeData !== null && inflectionSchemeModalName !== undefined">
    <TgModal
      dialog-class="inflection-scheme-modal"
      :content-class="`fonts__${articleFont}`"
      :size="size"
      :is-open="isInflectionSchemeOpen"
      @close="closeInflectionScheme()"
    >
      <template #modal-header>
        <span>
          <span class="inflection-scheme-modal__header-icon">
            <IconWhiteDocumentNoBorder class="text-primary" role="presentation" />
          </span>
          <span class="inflection-scheme-modal__header-text">
            <span v-lang:[inflectionLanguage]>{{ inflectionSchemeData.headWord }}&nbsp;</span>
            <i class="inflection-scheme-modal__header-word-information">
              <template v-if="gendersForLevel">
                {{ wordClassForLevel }}, {{ gendersForLevel }}
              </template>
              <template v-else>
                {{ wordClassForLevel }}
              </template>
            </i>
          </span>
        </span>
      </template>

      <Component
        :is="inflectionSchemeModalName"
        :lemma="inflectionSchemeData.headWord"
        :inflection-scheme="unwrappedInflectionScheme"
      />
    </TgModal>
  </div>
</template>
